import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import { Plus, Trash2 } from 'lucide-react';
import Text from '../../components/Text';
import Header from '../../components/Header';
import Actions from '../../components/Actions';
import Title from '../../components/Title';
import Line from '../../components/Line';
import List from '../../components/List';
import Tabs from '../../components/Tabs';
import Links from '../../components/Links';
import { nameWithDefault, itemsHaveContent } from '../../utils/helpers';
import Textarea from '../../components/Textarea';
import TabsCut from '../../components/TabsCut';
import TabsList from '../../components/TabsList';
import TabsListTitle from '../../components/TabsListTitle';
import TabsListItem from '../../components/TabsListItem';
import { STRATEGIES_MAX_COUNT } from '../../utils/const';
import Button from '../../components/Button';
import ConfirmationModal from '../../components/ConfirmationModal';
import SvgIcon from '../../components/SvgIcon';
import MobileMenuHeader from '../../components/MobileMenuHeader';

const csFields = [
  'name',
  'description',
  'frustrates',
  'goals',
  'problems',
  'type',
  'image',
];

const fields = ['name', 'description'];
const collectionName = 'getStrategies';
const icons = ['megaphone', 'text'];
const iconsComponents = icons.map((e) => <SvgIcon name={e} />);

export default function Get(props) {
  const {
    nextClickHandler,
    menuItems,
    selectComponent,
    componentIndex,
    rules,
    backPath,
    locales,
    locales: {
      caseStudies,
      common,
      tipCustomerSegment,
      getKeepGrow: {
        name,
        get: { header, title, text, list, tabs, customerSegmentsTitle },
      },
    },
  } = props;
  const customerSegments = useSelector(
    (state) => state.customerSegment.collection,
  );
  const { getStrategies } = useSelector((state) => state.getKeepGrow);
  const {
    getKeepGrow: { addStrategy, updateStrategy, removeStrategy },
  } = useDispatch();
  const [opened, setOpened] = useState(false);

  return (
    <section className="bmc-page" aria-label={header}>
      <Header
        title={header}
        subtitle={name}
        icon={<SvgIcon name="gkg" />}
        locales={locales}
        componentIndex={componentIndex}
        tipLocales={tipCustomerSegment}
        menuItems={menuItems}
        onMenuItemClick={selectComponent}
        rules={rules}
        backPath={backPath}
        mobileMenuHeader={
          <MobileMenuHeader icon="CRCanvas" title={name} color="green" />
        }
      />

      <Text align="center">{[text[0]]}</Text>

      <Title text={title[0]} />

      <List items={list} type="horizontal" icons={icons} />

      <Line />

      <h2 className="bmc-page-title">
        <span className="bmc-page-title--green">{title[1]}</span> {title[2]}
      </h2>

      <Text align="center">{[text[1]]}</Text>

      <div className="bmc-page-text bmc-page-text--center margin-top-40">
        <span className="bmc-page-text--bold">{title[0]} </span>
        <span>{title[1]}</span>
      </div>

      {itemsHaveContent(customerSegments, csFields) && (
        <div className="margin-top-40">
          <h4 className="font-bold text-md w-full text-center">
            {customerSegmentsTitle}
          </h4>
        </div>
      )}

      {itemsHaveContent(customerSegments, csFields) && (
        <Links items={customerSegments} />
      )}

      <Tabs
        tabs={[common.yourStrategy, common.caseStudy]}
        withBlur
        className="margin-top-40"
      >
        {[
          <div key="strategy">
            {getStrategies.map((strategy, index) => (
              <TabsList
                key={index}
                formStyle
                divider={index !== STRATEGIES_MAX_COUNT - 1}
              >
                <TabsListTitle className="flex flex-row justify-between items-center">
                  {nameWithDefault(strategy.name, name, index + 1)}

                  {getStrategies.length > 1 && (
                    <ConfirmationModal
                      className="ml-3 hover:bg-muted-lighter/60 text-muted"
                      body={locales.common.deleteConfirmation}
                      confirmText="Delete"
                      isBlockScroll
                      onConfirm={() =>
                        removeStrategy({ index, collectionName })
                      }
                    >
                      <Trash2 className="w-6 min-w-6 h-auto" />
                    </ConfirmationModal>
                  )}
                </TabsListTitle>

                {fields.map((field, i) => (
                  <TabsListItem
                    divider={false}
                    key={i}
                    title={tabs[field]}
                    subtitle={tabs[`${field}Sub`]}
                    icon={iconsComponents[i]}
                    className={`${i !== fields.length - 1 && 'mb-10'}`}
                    text={
                      // eslint-disable-next-line
                      <Textarea
                        delay={700}
                        placeholder={tabs[field]}
                        value={strategy[field]}
                        onChange={(event) =>
                          updateStrategy({
                            name: field,
                            value: event.target.value,
                            index,
                            collectionName,
                          })
                        }
                      />
                    }
                  />
                ))}
              </TabsList>
            ))}

            {getStrategies.length < STRATEGIES_MAX_COUNT && (
              <div className="bmc-page-tabs-content-actions margin-top-40">
                <Button
                  variant="outline"
                  className="bg-transparent hover:bg-secondary/50 border-secondary/50 text-secondary hover:text-secondary hover:border-secondary w-full"
                  onClick={() => addStrategy({ collectionName })}
                >
                  <Plus className="w-7 h-auto" />
                  {tabs.addNewStrategy}
                </Button>
              </div>
            )}
          </div>,

          <Tabs
            key="tabs"
            tabs={caseStudies.map((e) => e.name)}
            className="bmc-page-tabs--black bmc-page-tabs--inner"
          >
            {caseStudies.map((item, index) => (
              <div key={index}>
                <TabsCut
                  opened={opened}
                  setOpened={setOpened}
                  caseStudy={item}
                />

                {item.getStrategies.map((strategy, i) => (
                  <TabsList
                    key={i}
                    formStyle
                    preview
                    divider={i < item.getStrategies.length - 1}
                  >
                    <TabsListTitle>
                      {tabs.name} {i + 1}
                    </TabsListTitle>

                    {fields.map((field, ii) => (
                      <TabsListItem
                        divider={false}
                        className={`${ii < fields.length - 1 && 'mb-10'}`}
                        key={ii}
                        title={tabs[field]}
                        text={strategy[field]}
                        icon={iconsComponents[ii]}
                      />
                    ))}
                  </TabsList>
                ))}
              </div>
            ))}
          </Tabs>,
        ]}
      </Tabs>

      <Actions nextClickHandler={nextClickHandler} buttonName={common.next} />
    </section>
  );
}
